import { useState, useEffect, useRef, useMemo } from 'react'
import { Users, ChevronDown, ChevronUp } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import Paper from '@/components/Paper'
import { Button } from '@/components/ui/button'
import {
    DataTableCustomized,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { CustomerCompliance } from '@/modules/Service/types/sla'
import { formatPercent, meetsThreshold } from '../../Shared/utils/format-value'

const STORAGE_KEY = 'sla.dashboard.customers.expanded'

interface CustomersSectionInnerProps {
    customers: CustomerCompliance[]
    isLoading: boolean
    periodLabel: string
}

const CustomersSectionInner = ({
    customers,
    isLoading,
    periodLabel,
}: CustomersSectionInnerProps) => {
    // Persist expanded state in localStorage — collapsed by default
    const [isExpanded, setIsExpanded] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) === '1'
        } catch {
            return false
        }
    })

    const toggle = () => {
        setIsExpanded((prev) => {
            const next = !prev
            try {
                localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
            } catch {
                // localStorage unavailable — silent
            }
            return next
        })
    }

    const columns = useMemo(
        (): DataTableColumn<CustomerCompliance>[] => [
            {
                key: 'customer_name',
                title: 'Khách hàng',
                render: (_val, row) => (
                    <span className="font-medium">{row.customer_name}</span>
                ),
            },
            {
                key: 'service_count',
                title: 'Dịch vụ',
                headerClassName: 'text-center',
                cellClassName: 'text-center text-sm text-gray-500',
                render: (_val, row) => row.service_count,
            },
            {
                key: 'response_met' as keyof CustomerCompliance,
                title: 'Phản hồi',
                headerClassName: 'text-center',
                cellClassName: 'text-center',
                render: (_val, row) => (
                    <div className="flex items-center justify-center gap-1 text-xs">
                        <span className="text-green-600">
                            {row.response_met}
                        </span>
                        <span className="text-gray-400">/</span>
                        <span className="text-red-600">
                            {row.response_breached}
                        </span>
                    </div>
                ),
            },
            {
                key: 'resolution_met' as keyof CustomerCompliance,
                title: 'Xử lý',
                headerClassName: 'text-center',
                cellClassName: 'text-center',
                render: (_val, row) => (
                    <div className="flex items-center justify-center gap-1 text-xs">
                        <span className="text-green-600">
                            {row.resolution_met}
                        </span>
                        <span className="text-gray-400">/</span>
                        <span className="text-red-600">
                            {row.resolution_breached}
                        </span>
                    </div>
                ),
            },
            {
                key: 'avg_compliance_pct',
                title: 'Tuân thủ',
                headerClassName: 'text-center',
                cellClassName: 'text-center',
                render: (_val, row) => (
                    <Badge
                        className={`text-xs ${
                            meetsThreshold(row.avg_compliance_pct, 95)
                                ? 'bg-green-100 text-green-700'
                                : meetsThreshold(row.avg_compliance_pct, 80)
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                        }`}
                    >
                        {formatPercent(row.avg_compliance_pct, 'ui', 1)}
                    </Badge>
                ),
            },
        ],
        []
    )

    if (isLoading) {
        return <Skeleton className="h-48 w-full" />
    }

    if (customers.length === 0) {
        return null
    }

    return (
        <Paper>
            <div className="py-3 px-4">
                <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={toggle}
                >
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-indigo-500" />
                        <p className="text-sm font-medium">
                            Tuân Thủ Theo Khách Hàng · {periodLabel}
                        </p>
                        <Badge variant="outline" className="text-xs">
                            {customers.length}
                        </Badge>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                        ) : (
                            <ChevronDown className="w-4 h-4" />
                        )}
                    </Button>
                </div>
                {!isExpanded && (
                    <p className="text-xs text-gray-500 mt-1">
                        Sắp xếp theo mức độ tuân thủ (thấp nhất trước) — nhấn để
                        mở
                    </p>
                )}
            </div>
            {isExpanded && (
                <DataTableCustomized<CustomerCompliance>
                    columns={columns}
                    rows={customers}
                    rowKey={(row) => row.customer_id}
                    rowClassName={(row) =>
                        row.avg_compliance_pct < 95 ? 'bg-red-50/30' : ''
                    }
                    size="small"
                    bordered
                />
            )}
        </Paper>
    )
}

interface CustomersSectionProps {
    customers: CustomerCompliance[]
    isLoading: boolean
    periodLabel: string
}

/** Lazy-mount via IntersectionObserver */
const CustomersSection = ({
    customers,
    isLoading,
    periodLabel,
}: CustomersSectionProps) => {
    const [mounted, setMounted] = useState(false)
    const sentinelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = sentinelRef.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setMounted(true)
                    observer.disconnect()
                }
            },
            { rootMargin: '200px' }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <div ref={sentinelRef}>
            {mounted ? (
                <CustomersSectionInner
                    customers={customers}
                    isLoading={isLoading}
                    periodLabel={periodLabel}
                />
            ) : (
                <Skeleton className="h-16 w-full" />
            )}
        </div>
    )
}

export default CustomersSection
